import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Tag, Trash2, Edit, Download, ChevronDown, ChevronUp, BarChart3, Grid3X3, List, CheckCircle, XCircle } from 'lucide-react';
import { useSettings } from '@/contexts/SettingsContext';
import { useScreenSize } from '@/hooks/useScreenSize';
import { ExpenseFormDialog } from '@/components/dialogs/ExpenseFormDialog';
import { CategoryFormDialog } from '@/components/expenses/CategoryFormDialog';
import { ExpenseCharts } from '@/components/expenses/ExpenseCharts';
import { ExpenseTableView, ExpenseCardView } from '@/components/expenses/ExpenseListView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Depenses = ({ user }) => {
  const { settings } = useSettings();
  const { isMobile } = useScreenSize();
  const currencySymbol = settings.currency_symbol || 'DT';
  const itemsPerPage = useMemo(() => settings.items_per_page || 10, [settings.items_per_page]);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  
  const [sortBy, setSortBy] = useState('date-desc');
  const [filterCategory, setFilterCategory] = useState('tous');
  const [filterPaye, setFilterPaye] = useState('tous');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('table');
  
  const [categoriesVisible, setCategoriesVisible] = useState(false);
  const [chartsVisible, setChartsVisible] = useState(false);
  const [chartType, setChartType] = useState('bars');
  
  const [expenseFormData, setExpenseFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    categorie_id: '', montant: '', description: '', quantite: '', unite: '', paye: false, update_stock: false,
  });
  const [stocks, setStocks] = useState([]);
  const [categoryFormData, setCategoryFormData] = useState({ nom: '', description: '', couleur: '#10B981', is_alimentation: false, seuil: '' });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editCategoryDialogOpen, setEditCategoryDialogOpen] = useState(false);

  const canWrite = user?.permissions?.includes('write') || user?.role === 'admin';
  const canDelete = user?.permissions?.includes('delete') || user?.role === 'admin';

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (isMobile && viewMode === 'table') setViewMode('cards');
  }, [isMobile, viewMode]);

  const filteredExpenses = useMemo(() => {
    let filtered = [...expenses];
    if (filterCategory !== 'tous') filtered = filtered.filter(exp => exp.categorie_id === filterCategory);
    if (filterPaye === 'paye') filtered = filtered.filter(exp => exp.paye === true);
    else if (filterPaye === 'non_paye') filtered = filtered.filter(exp => !exp.paye);
    
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc': return new Date(b.date) - new Date(a.date);
        case 'date-asc': return new Date(a.date) - new Date(b.date);
        case 'category':
          return (categories.find(c => c.id === a.categorie_id)?.nom || '').localeCompare(categories.find(c => c.id === b.categorie_id)?.nom || '');
        case 'amount-desc': return b.montant - a.montant;
        case 'amount-asc': return a.montant - b.montant;
        default: return new Date(b.date) - new Date(a.date);
      }
    });
    return filtered;
  }, [expenses, categories, sortBy, filterCategory, filterPaye]);

  useEffect(() => { setCurrentPage(1); }, [sortBy, filterCategory, filterPaye]);

  const fetchData = useCallback(async () => {
    try {
      const [expensesRes, categoriesRes, stocksRes] = await Promise.all([
        axios.get(`${API}/expenses`), axios.get(`${API}/expense-categories`), axios.get(`${API}/food-stock`),
      ]);
      setExpenses(expensesRes.data);
      setCategories(categoriesRes.data.map(c => ({ ...c, nom: c.name || c.nom, couleur: c.color || c.couleur })));
      setStocks(stocksRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  const openExpenseDialog = (expense = null) => {
    if (expense) {
      setSelectedExpense(expense);
      setExpenseFormData({
        date: expense.date, categorie_id: expense.categorie_id, montant: expense.montant.toString(),
        description: expense.description || '', quantite: expense.quantite?.toString() || '', unite: expense.unite || '', paye: expense.paye || false,
      });
    } else {
      setSelectedExpense(null);
      setExpenseFormData({ date: new Date().toISOString().split('T')[0], categorie_id: '', montant: '', description: '', quantite: '', unite: '', paye: false, stock_id: '' });
    }
    setExpenseDialogOpen(true);
  };

  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedExpenses = filteredExpenses.slice(startIndex, startIndex + itemsPerPage);

  const expensesByCategory = categories.map(cat => {
    const categoryExpenses = filteredExpenses.filter(exp => exp.categorie_id === cat.id);
    const total = categoryExpenses.reduce((sum, exp) => sum + exp.montant, 0);
    return { name: cat.nom, value: total, count: categoryExpenses.length, color: cat.couleur };
  }).filter(cat => cat.value > 0).sort((a, b) => b.value - a.value);

  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + exp.montant, 0);

  const exportToPDF = () => {
    const htmlContent = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Rapport Dépenses</title>
<style>body{font-family:Arial,sans-serif;margin:20px;color:#333}.header{text-align:center;margin-bottom:30px}.summary{background:#f8fafc;padding:15px;border-radius:8px;margin:20px 0}table{width:100%;border-collapse:collapse;margin-top:20px}th,td{border:1px solid #ddd;padding:12px;text-align:left}th{background-color:#334155;color:white}.total-row{background-color:#f8fafc;font-weight:bold}.category{display:inline-block;width:12px;height:12px;border-radius:50%;margin-right:8px}</style>
</head><body><div class="header"><h2>Rapport des Dépenses</h2><p><strong>Généré le:</strong> ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p></div>
<div class="summary"><p><strong>Total des dépenses:</strong> ${totalExpenses.toFixed(2)} ${currencySymbol}</p><p><strong>Nombre de dépenses:</strong> ${filteredExpenses.length}</p></div>
<table><thead><tr><th>Date</th><th>Catégorie</th><th>Description</th><th>Quantité</th><th>Montant (${currencySymbol})</th></tr></thead><tbody>
${filteredExpenses.map(expense => { const cat = categories.find(c => c.id === expense.categorie_id); return `<tr><td>${new Date(expense.date).toLocaleDateString('fr-FR')}</td><td>${cat ? `<span class="category" style="background-color:${cat.couleur}"></span>${cat.nom}` : 'Inconnue'}</td><td>${expense.description || '-'}</td><td>${expense.quantite ? `${expense.quantite} ${expense.unite || ''}` : '-'}</td><td style="text-align:right">${expense.montant.toFixed(2)}</td></tr>`; }).join('')}
<tr class="total-row"><td colspan="4"><strong>TOTAL GÉNÉRAL</strong></td><td style="text-align:right"><strong>${totalExpenses.toFixed(2)}</strong></td></tr></tbody></table></body></html>`;
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `rapport_depenses_${new Date().toISOString().split('T')[0]}.html`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success('Rapport PDF généré avec succès (format HTML)');
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!canWrite) { toast.error('Vous n\'avez pas la permission d\'effectuer cette action'); return; }
    try {
      const data = { ...expenseFormData, montant: parseFloat(expenseFormData.montant), quantite: expenseFormData.quantite ? parseFloat(expenseFormData.quantite) : null, paye: expenseFormData.paye, update_stock: expenseFormData.update_stock || false };
      if (selectedExpense) { await axios.put(`${API}/expenses/${selectedExpense.id}`, data); toast.success('Dépense modifiée avec succès'); }
      else { await axios.post(`${API}/expenses`, data); toast.success('Dépense ajoutée avec succès'); }
      fetchData();
      setExpenseDialogOpen(false);
      setExpenseFormData({ date: new Date().toISOString().split('T')[0], categorie_id: '', montant: '', description: '', quantite: '', unite: '', paye: false, update_stock: false });
      setSelectedExpense(null);
    } catch (error) {
      let errorMessage = 'Erreur lors de l\'enregistrement';
      if (error.response?.data) {
        const d = error.response.data;
        if (typeof d.detail === 'string') errorMessage = d.detail;
        else if (Array.isArray(d.detail)) errorMessage = d.detail.map(err => err.msg || 'Erreur de validation').join(', ');
        else if (d.message) errorMessage = d.message;
      }
      toast.error(errorMessage);
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { name: categoryFormData.nom, color: categoryFormData.couleur || '#6B7280', is_alimentation: categoryFormData.is_alimentation, seuil: categoryFormData.seuil ? parseFloat(categoryFormData.seuil) : null };
      await axios.post(`${API}/expense-categories`, data);
      toast.success('Catégorie ajoutée avec succès');
      fetchData();
      setCategoryDialogOpen(false);
      setCategoryFormData({ nom: '', description: '', couleur: '#10B981', is_alimentation: false, seuil: '' });
    } catch (error) { toast.error('Erreur lors de l\'enregistrement'); }
  };

  const openEditCategoryDialog = (category) => {
    setSelectedCategory(category);
    setCategoryFormData({ nom: category.name || category.nom, description: category.description || '', couleur: category.color || category.couleur || '#10B981', is_alimentation: category.is_alimentation || false, seuil: category.seuil?.toString() || '' });
    setEditCategoryDialogOpen(true);
  };

  const handleEditCategorySubmit = async (e) => {
    e.preventDefault();
    if (!canWrite) { toast.error('Vous n\'avez pas la permission d\'effectuer cette action'); return; }
    try {
      const data = { name: categoryFormData.nom, color: categoryFormData.couleur || '#6B7280', is_alimentation: categoryFormData.is_alimentation, seuil: categoryFormData.seuil ? parseFloat(categoryFormData.seuil) : null };
      await axios.put(`${API}/expense-categories/${selectedCategory.id}`, data);
      toast.success('Catégorie modifiée avec succès');
      fetchData();
      setEditCategoryDialogOpen(false);
      setSelectedCategory(null);
      setCategoryFormData({ nom: '', description: '', couleur: '#10B981', is_alimentation: false, seuil: '' });
    } catch (error) { toast.error('Erreur lors de la modification'); }
  };

  const handleDeleteExpense = async (id) => {
    if (!canDelete) { toast.error('Vous n\'avez pas la permission de supprimer'); return; }
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette dépense ?')) {
      try { await axios.delete(`${API}/expenses/${id}`); toast.success('Dépense supprimée avec succès'); fetchData(); }
      catch (error) { toast.error('Erreur lors de la suppression'); }
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!canDelete) { toast.error('Vous n\'avez pas la permission de supprimer'); return; }
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
      try { await axios.delete(`${API}/expense-categories/${id}`); toast.success('Catégorie supprimée avec succès'); fetchData(); }
      catch (error) { toast.error('Erreur lors de la suppression'); }
    }
  };

  const getCategoryById = (id) => categories.find((cat) => cat.id === id);

  const handleTogglePaye = async (expense) => {
    if (!canWrite) { toast.error('Vous n\'avez pas la permission d\'effectuer cette action'); return; }
    try {
      const updatedData = { date: expense.date, categorie_id: expense.categorie_id, montant: expense.montant, description: expense.description || '', quantite: expense.quantite || null, unite: expense.unite || null, paye: !expense.paye };
      await axios.put(`${API}/expenses/${expense.id}`, updatedData);
      toast.success(updatedData.paye ? 'Dépense marquée comme payée' : 'Dépense marquée comme non payée');
      fetchData();
    } catch (error) { toast.error('Erreur lors de la mise à jour du statut'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="spinner"></div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="depenses-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Gestion des Dépenses</h1>
        <div className="flex gap-2">
          <Button onClick={exportToPDF} variant="outline" className="border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-950" data-testid="export-pdf-button">
            <Download className="w-4 h-4 mr-2" />Export PDF
          </Button>
          {canWrite && (
            <Button onClick={() => openExpenseDialog()} className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600" data-testid="add-expense-button">
              <Plus className="w-4 h-4 mr-2" />Ajouter une dépense
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md border-l-4 border-l-blue-500"><CardContent className="pt-6"><div className="flex items-center"><Tag className="w-8 h-8 text-blue-600 dark:text-blue-400" /><div className="ml-4"><p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total dépenses</p><p className="text-2xl font-bold text-blue-600 dark:text-blue-400" data-testid="stat-total-expenses">{expenses.length}</p></div></div></CardContent></Card>
        <Card className="border-0 shadow-md border-l-4 border-l-slate-500"><CardContent className="pt-6"><div className="flex items-center"><Tag className="w-8 h-8 text-slate-700 dark:text-slate-300" /><div className="ml-4"><p className="text-sm font-medium text-gray-600 dark:text-gray-400">Montant total</p><p className="text-2xl font-bold text-slate-700 dark:text-slate-300" data-testid="stat-total-amount">{totalExpenses.toFixed(2)} {currencySymbol}</p></div></div></CardContent></Card>
        <Card className="border-0 shadow-md border-l-4 border-l-green-500"><CardContent className="pt-6"><div className="flex items-center"><CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" /><div className="ml-4"><p className="text-sm font-medium text-gray-600 dark:text-gray-400">Payé</p><p className="text-2xl font-bold text-green-600 dark:text-green-400" data-testid="stat-paid">{expenses.filter(e => e.paye).reduce((sum, e) => sum + e.montant, 0).toFixed(2)} {currencySymbol}</p></div></div></CardContent></Card>
        <Card className="border-0 shadow-md border-l-4 border-l-amber-500"><CardContent className="pt-6"><div className="flex items-center"><XCircle className="w-8 h-8 text-amber-600 dark:text-amber-400" /><div className="ml-4"><p className="text-sm font-medium text-gray-600 dark:text-gray-400">Non payé</p><p className="text-2xl font-bold text-amber-600 dark:text-amber-400" data-testid="stat-unpaid">{expenses.filter(e => !e.paye).reduce((sum, e) => sum + e.montant, 0).toFixed(2)} {currencySymbol}</p></div></div></CardContent></Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Filtrer par catégorie</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="tous">Toutes les catégories</SelectItem>{categories.map(cat => <SelectItem key={cat.id} value={cat.id}>{cat.nom}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-2">
              <Label>Statut paiement</Label>
              <Select value={filterPaye} onValueChange={setFilterPaye}><SelectTrigger data-testid="filter-paye-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="tous">Tous</SelectItem><SelectItem value="paye">Payées</SelectItem><SelectItem value="non_paye">Non payées</SelectItem></SelectContent></Select>
            </div>
            <div className="space-y-2">
              <Label>Trier par</Label>
              <Select value={sortBy} onValueChange={setSortBy}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="date-desc">Date (récente)</SelectItem><SelectItem value="date-asc">Date (ancienne)</SelectItem><SelectItem value="category">Catégorie</SelectItem><SelectItem value="amount-desc">Montant (haut)</SelectItem><SelectItem value="amount-asc">Montant (bas)</SelectItem></SelectContent></Select>
            </div>
            <div className="space-y-2">
              <Label>Affichage</Label>
              <div className="flex gap-1">
                {!isMobile && <Button type="button" variant={viewMode === 'table' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('table')} data-testid="table-view-button" title="Vue tableau"><List className="w-4 h-4" /></Button>}
                <Button type="button" variant={viewMode === 'cards' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('cards')} data-testid="cards-view-button" title="Vue cartes"><Grid3X3 className="w-4 h-4" /></Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Résultats</Label>
              <div className="h-10 flex items-center px-3 bg-gray-50 dark:bg-gray-950 rounded-lg border"><span className="text-sm font-medium text-gray-700 dark:text-gray-300">{filteredExpenses.length} dépense(s)</span></div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setCategoriesVisible(!categoriesVisible)} className="flex-1 justify-between"><div className="flex items-center gap-2"><Tag className="w-4 h-4" />Catégories</div>{categoriesVisible ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</Button>
            <Button variant="outline" size="sm" onClick={() => setChartsVisible(!chartsVisible)} className="flex-1 justify-between"><div className="flex items-center gap-2"><BarChart3 className="w-4 h-4" />Graphiques</div>{chartsVisible ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</Button>
          </div>
        </CardContent>
      </Card>

      {/* Categories Section */}
      {categoriesVisible && (
        <Card className="border-0 shadow-md">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2"><Tag className="w-5 h-5 text-slate-700 dark:text-slate-300" />Catégories de dépenses</CardTitle>
              {canWrite && <Button onClick={() => setCategoryDialogOpen(true)} variant="outline" size="sm" className="border-gray-200 dark:border-gray-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:bg-slate-900/30" data-testid="add-category-button"><Plus className="w-4 h-4 mr-2" />Ajouter une catégorie</Button>}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {categories.map(cat => (
                <div key={cat.id} className="p-4 border-l-4 bg-gray-50 dark:bg-gray-950 rounded-lg hover:shadow-md transition-all group" style={{ borderLeftColor: cat.couleur }} data-testid={`category-${cat.nom}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">{cat.nom}</span>
                        {cat.is_alimentation && <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">Alimentation</span>}
                      </div>
                      {cat.description && <div className="text-xs text-gray-600 mt-1">{cat.description}</div>}
                      <div className="text-xs text-gray-500 mt-2">
                        {expenses.filter(e => e.categorie_id === cat.id).length} dépense(s) {expenses.filter(e => e.categorie_id === cat.id).reduce((sum, e) => sum + e.montant, 0).toFixed(2)} {currencySymbol}
                        {cat.is_alimentation && cat.seuil && <span className="ml-2 text-blue-600">Seuil: {cat.seuil}</span>}
                      </div>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {canWrite && <Button variant="ghost" size="sm" onClick={() => openEditCategoryDialog(cat)} className="text-blue-500 hover:bg-blue-50" data-testid={`edit-category-${cat.nom}`}><Edit className="w-4 h-4" /></Button>}
                      {canDelete && <Button variant="ghost" size="sm" onClick={() => handleDeleteCategory(cat.id)} className="text-red-500 hover:bg-red-50" data-testid={`delete-category-${cat.nom}`}><Trash2 className="w-4 h-4" /></Button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <ExpenseCharts visible={chartsVisible} chartType={chartType} setChartType={setChartType} data={expensesByCategory} total={totalExpenses} currencySymbol={currencySymbol} />

      {/* Table View */}
      {viewMode === 'table' && (
        <ExpenseTableView expenses={paginatedExpenses} getCategoryById={getCategoryById} currencySymbol={currencySymbol} canWrite={canWrite} canDelete={canDelete} onEdit={openExpenseDialog} onDelete={handleDeleteExpense} onTogglePaye={handleTogglePaye} currentPage={currentPage} totalPages={totalPages} startIndex={startIndex} itemsPerPage={itemsPerPage} totalItems={filteredExpenses.length} onPageChange={setCurrentPage} />
      )}

      {/* Card View */}
      {viewMode === 'cards' && (
        <ExpenseCardView expenses={paginatedExpenses} getCategoryById={getCategoryById} currencySymbol={currencySymbol} canWrite={canWrite} canDelete={canDelete} onEdit={openExpenseDialog} onDelete={handleDeleteExpense} onTogglePaye={handleTogglePaye} currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      )}

      {/* Dialogs */}
      <ExpenseFormDialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen} selectedExpense={selectedExpense} formData={expenseFormData} setFormData={setExpenseFormData} onSubmit={handleExpenseSubmit} categories={categories} currencySymbol={currencySymbol} />
      <CategoryFormDialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen} formData={categoryFormData} setFormData={setCategoryFormData} onSubmit={handleCategorySubmit} />
      <CategoryFormDialog open={editCategoryDialogOpen} onOpenChange={setEditCategoryDialogOpen} formData={categoryFormData} setFormData={setCategoryFormData} onSubmit={handleEditCategorySubmit} isEdit />
    </div>
  );
};

export default Depenses;
